import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Avatar,
  IconButton,
  CardMedia,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import CloseIcon from "@mui/icons-material/Close";
import { convertUTCToLocal } from "@/lib/utils/dateUtils";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

// Parse announcement body into structured list
const parseAnnouncementBody = (body) => {
  if (!body) return [{ type: "text", content: "" }];

  const lines = body.split("\n").filter((line) => line.trim());
  const result = [];
  let currentList = null;

  lines.forEach((line) => {
    const trimmedLine = line.trim();
    // Match numbered paragraphs (e.g., "1.", "2.")
    const numberedMatch = trimmedLine.match(/^(\d+)\.\s*(.+)$/);
    // Match sub-points (e.g., "Risk Assessment: ...")
    const subPointMatch = trimmedLine.match(/^([A-Za-z][^:]+):\s*(.+)$/);

    if (numberedMatch) {
      // End previous list if exists
      if (currentList) {
        result.push(currentList);
        currentList = null;
      }
      // Start new numbered list
      currentList = {
        type: "numbered",
        items: [{ title: numberedMatch[2], subItems: [] }],
      };
    } else if (
      subPointMatch &&
      currentList &&
      currentList.type === "numbered"
    ) {
      // Add sub-point to current numbered list
      currentList.items[currentList.items.length - 1].subItems.push({
        title: subPointMatch[1],
        content: subPointMatch[2],
      });
    } else {
      // End current list and add text
      if (currentList) {
        result.push(currentList);
        currentList = null;
      }
      result.push({ type: "text", content: line });
    }
  });

  // Push final list if exists
  if (currentList) {
    result.push(currentList);
  }

  return result;
};

function AnnouncementModal({ open, onClose, announcement, onImageClick }) {
  const { t } = useTranslation();

  if (!announcement) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="announcement-dialog-title"
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle sx={{ p: 3 }} id="announcement-dialog-title">
        {announcement.title || t("news.unknownTitle")}
        <IconButton
          aria-label={t("news.closeModalAria")}
          onClick={onClose}
          sx={{ position: "absolute", right: 16, top: 16 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 3, bgcolor: "background.paper" }}>
        <Box display="flex" alignItems="center" mb={2}>
          <Avatar
            src={announcement.author.avatar_url}
            alt={t("news.authorAvatarAlt", {
              name: announcement.author.first_name || t("news.unknownAuthor"),
            })}
            sx={{ width: 48, height: 48, mr: 2 }}
            aria-label={t("news.authorAvatarAria")}
          />
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              {announcement.author.first_name || t("news.unknownAuthor")}{" "}
              {announcement.author.last_name || t("news.unknownAuthor")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {convertUTCToLocal(announcement.created_at) ||
                t("news.unknownDate")}
            </Typography>
          </Box>
        </Box>

        {/* Render Parsed Body */}
        {parseAnnouncementBody(announcement.body).map((section, index) => {
          if (section.type === "text") {
            return (
              <Typography
                key={index}
                variant="body1"
                component="p"
                sx={{ whiteSpace: "pre-line", mb: 2 }}
              >
                {section.content || t("news.unknownBody")}
              </Typography>
            );
          } else if (section.type === "numbered") {
            return (
              <List
                key={index}
                component="ol"
                sx={{ listStyleType: "decimal", pl: 4 }}
              >
                {section.items.map((item, itemIndex) => (
                  <ListItem
                    key={itemIndex}
                    sx={{ display: "list-item", py: 0.5 }}
                  >
                    <ListItemText
                      primary={item.title}
                      slotProps={{
                        primary: {
                          variant: "body1",
                          fontWeight: "bold",
                          component: "span",
                        },
                        secondary: {
                          component: "div",
                        },
                      }}
                      secondary={
                        item.subItems.length > 0 && (
                          <List
                            component="ul"
                            sx={{ listStyleType: "disc", pl: 4, mt: 1 }}
                          >
                            {item.subItems.map((subItem, subIndex) => (
                              <ListItem
                                key={subIndex}
                                sx={{ display: "list-item", py: 0.5 }}
                              >
                                <ListItemText
                                  primary={`${subItem.title}: ${subItem.content}`}
                                  slotProps={{
                                    primary: {
                                      variant: "body2",
                                      component: "span",
                                    },
                                  }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        )
                      }
                    />
                  </ListItem>
                ))}
              </List>
            );
          }
          return null;
        })}

        {/* Attachments */}
        <Grid container spacing={2}>
          {announcement.attachments?.map(
            (attachment) =>
              attachment.is_image && (
                <Grid size={{ xs: 12 }} key={attachment.id}>
                  <CardMedia
                    component="img"
                    src={attachment.url}
                    alt={t("news.imageAlt", {
                      name: attachment.display_name,
                    })}
                    sx={{
                      borderRadius: 2,
                      cursor: "pointer",
                      boxShadow: 3,
                      "&:hover": { opacity: 0.8 },
                    }}
                    onClick={() => onImageClick(announcement, attachment)}
                    aria-label={t("news.imageAria", {
                      name: attachment.display_name,
                    })}
                  />
                </Grid>
              )
          )}
        </Grid>
      </DialogContent>
    </Dialog>
  );
}

AnnouncementModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  announcement: PropTypes.shape({
    title: PropTypes.string,
    body: PropTypes.string,
    created_at: PropTypes.string,
    author: PropTypes.shape({
      first_name: PropTypes.string,
      last_name: PropTypes.string,
      avatar_url: PropTypes.string,
    }),
    attachments: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        url: PropTypes.string,
        is_image: PropTypes.bool,
        display_name: PropTypes.string,
      })
    ),
  }),
  onImageClick: PropTypes.func.isRequired,
};

export default AnnouncementModal;
